import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ClipboardList,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MoreHorizontal,
  Moon,
  ScrollText,
  Smartphone,
  Sun,
  Tags,
  Workflow,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { api } from '../api/cliente.js';
import { BannerDesconectado } from '../componentes/estados.js';
import { useSessao } from '../sessao.js';

/**
 * Layout do painel.
 *
 * Sidebar fixa de 200px no desktop e tab bar inferior abaixo de 680px, como no
 * handoff. Auditoria e Administração só aparecem para administrador — esconder
 * é conveniência; o servidor recusa de qualquer forma.
 */

interface ItemMenu {
  rota: string;
  rotulo: string;
  icone: ReactNode;
  soAdmin?: boolean;
  /** Mostra contador quando houver pendências. */
  badge?: number;
}

export function Layout({ children }: { children: ReactNode }) {
  const [local, navegar] = useLocation();
  const { usuario, sair, ehAdmin } = useSessao();
  const [tema, setTema] = useState<'claro' | 'escuro'>(
    () => (localStorage.getItem('tf_tema') as 'claro' | 'escuro') ?? 'claro',
  );

  useEffect(() => {
    document.documentElement.dataset.tema = tema;
    localStorage.setItem('tf_tema', tema);
  }, [tema]);

  // Polling de 10s, como o handoff pede para o status do WhatsApp.
  const { data: conexao } = useQuery({
    queryKey: ['conexao-status'],
    queryFn: () => api.statusConexao(),
    refetchInterval: 10_000,
  });

  const { data: revisao } = useQuery({
    queryKey: ['revisao'],
    queryFn: () => api.revisao(),
    refetchInterval: 30_000,
  });

  const pendentes = revisao?.pendentes.length ?? 0;
  const desconectado = conexao !== undefined && conexao.status !== 'conectado';

  const itens: ItemMenu[] = [
    { rota: '/', rotulo: 'Dashboard', icone: <LayoutDashboard size={15} /> },
    { rota: '/transacoes', rotulo: 'Transações', icone: <ListChecks size={15} /> },
    { rota: '/entradas', rotulo: 'Entradas', icone: <ArrowUpCircle size={15} /> },
    { rota: '/saidas', rotulo: 'Saídas', icone: <ArrowDownCircle size={15} /> },
    { rota: '/revisao', rotulo: 'Revisão', icone: <ClipboardList size={15} />, badge: pendentes },
    { rota: '/categorias', rotulo: 'Categorias', icone: <Tags size={15} /> },
    { rota: '/conexao', rotulo: 'Conexão', icone: <Smartphone size={15} /> },
    { rota: '/automacao', rotulo: 'Automação', icone: <Workflow size={15} />, soAdmin: true },
    { rota: '/auditoria', rotulo: 'Auditoria', icone: <ScrollText size={15} />, soAdmin: true },
  ];

  const visiveis = itens.filter((i) => !i.soAdmin || ehAdmin);

  return (
    <div className="flex min-h-screen bg-fundo">
      {/* Sidebar — desktop */}
      <aside className="hidden w-[var(--spacing-sidebar)] shrink-0 flex-col border-r border-borda bg-superficie md:flex">
        <div className="border-b border-borda-sutil px-4 py-4">
          <p className="font-semibold text-texto">True Finance</p>
          <p className="mono text-[length:var(--text-metadado)] text-texto-terciario">v0.1</p>
        </div>

        <nav className="flex-1 p-2">
          {visiveis.map((item) => (
            <ItemNav key={item.rota} item={item} ativo={local === item.rota} />
          ))}
        </nav>

        <div className="border-t border-borda-sutil p-3">
          <button
            type="button"
            onClick={() => setTema((t) => (t === 'claro' ? 'escuro' : 'claro'))}
            className="mb-2 flex w-full items-center gap-2 rounded-[var(--radius-controle)] px-2 py-1.5 text-[length:var(--text-secundario)] text-texto-secundario hover:bg-fundo"
          >
            {tema === 'claro' ? <Moon size={14} /> : <Sun size={14} />}
            Modo {tema === 'claro' ? 'escuro' : 'claro'}
          </button>

          <div className="flex items-center gap-2 px-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--cor-card-escuro)] text-[length:var(--text-metadado)] font-semibold text-white">
              {usuario?.nome.slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[length:var(--text-secundario)] text-texto">
                {usuario?.nome}
              </p>
              <p className="text-[length:var(--text-metadado)] uppercase text-texto-terciario">
                {usuario?.papel}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void sair()}
              aria-label="Sair"
              className="text-texto-terciario hover:text-saida"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col pb-16 md:pb-0">
        {desconectado && <BannerDesconectado aoIrParaConexao={() => navegar('/conexao')} />}
        <main className="flex-1 px-5 py-5 md:px-6">{children}</main>
      </div>

      {/* Tab bar — mobile, abaixo de 680px */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-borda bg-superficie md:hidden">
        {visiveis.slice(0, 3).map((item) => (
          <ItemTab key={item.rota} item={item} ativo={local === item.rota} />
        ))}
        <ItemTab
          item={{
            rota: '/revisao',
            rotulo: 'Revisão',
            icone: <ClipboardList size={17} />,
            badge: pendentes,
          }}
          ativo={local === '/revisao'}
        />
        <ItemTab
          item={{ rota: '/conexao', rotulo: 'Mais', icone: <MoreHorizontal size={17} /> }}
          ativo={local === '/conexao'}
        />
      </nav>
    </div>
  );
}

function ItemNav({ item, ativo }: { item: ItemMenu; ativo: boolean }) {
  return (
    <Link
      href={item.rota}
      className={`mb-0.5 flex items-center gap-2 rounded-[var(--radius-controle)] px-2 py-2 text-[length:var(--text-secundario)] ${
        ativo
          ? 'bg-primaria-bg font-semibold text-primaria-fg'
          : 'text-texto-secundario hover:bg-fundo'
      }`}
    >
      {item.icone}
      <span className="flex-1">{item.rotulo}</span>
      {item.badge ? (
        <span className="rounded-full bg-pendente px-1.5 py-0.5 text-[10px] font-bold text-white">
          {item.badge}
        </span>
      ) : null}
    </Link>
  );
}

function ItemTab({ item, ativo }: { item: ItemMenu; ativo: boolean }) {
  return (
    <Link
      href={item.rota}
      // Alvo de toque de 44px, exigência de acessibilidade do handoff.
      className={`relative flex min-h-[44px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] ${
        ativo ? 'text-primaria' : 'text-texto-terciario'
      }`}
    >
      {item.icone}
      {item.rotulo}
      {item.badge ? (
        <span className="absolute right-1/4 top-1 rounded-full bg-pendente px-1 text-[9px] font-bold text-white">
          {item.badge}
        </span>
      ) : null}
    </Link>
  );
}
